import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Sheet } from 'konsta/react';
import { ChevronLeft, MapPin, Star } from 'lucide-react';
import type { ProviderCard } from '../api';
import { useName, useT } from '../i18n';
import { useNav } from '../nav';

export const etb = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

/** Round photo, or the first letter of the name on a coloured circle. */
export function Avatar({ src, name, size = 44, ring }: { src?: string | null; name: string; size?: number; ring?: boolean }) {
  const palette = ['#f97316', '#8b5cf6', '#10b981', '#ec4899', '#0ea5e9', '#eab308'];
  const color = palette[(name.charCodeAt(0) || 0) % palette.length];
  const inner = src ? (
    <img src={src} alt="" className="h-full w-full rounded-full object-cover" loading="lazy" />
  ) : (
    <span className="flex h-full w-full items-center justify-center rounded-full font-semibold text-white" style={{ background: color, fontSize: size * 0.42 }}>
      {name.slice(0, 1).toUpperCase() || '?'}
    </span>
  );
  return (
    <span
      className={`inline-block shrink-0 rounded-full ${ring ? 'p-[2.5px]' : ''}`}
      style={{ width: size, height: size, background: ring ? 'linear-gradient(45deg,#f59e0b,#ec4899,#8b5cf6)' : undefined }}
    >
      <span className={`block h-full w-full rounded-full ${ring ? 'border-2 border-tg-bg' : ''}`}>{inner}</span>
    </span>
  );
}

export function Rating({ avg, count }: { avg: number | null; count: number }) {
  const t = useT();
  if (avg === null || !count) return <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-xs font-semibold text-green-700">{t('provider.new')}</span>;
  return (
    <span className="inline-flex items-center gap-1 text-sm font-semibold">
      <Star size={14} className="fill-amber-400 text-amber-400" />
      {avg.toFixed(1)}
      <span className="font-normal text-tg-hint">({count})</span>
    </span>
  );
}

/** "2.4 km away", "Under 1 km", or the area/city name when distance is unknown. */
export function Where({ p, compact }: { p: Pick<ProviderCard, 'distanceKm' | 'area' | 'city'>; compact?: boolean }) {
  const t = useT();
  const name = useName();
  const place = p.area ? name(p.area) : p.city ? name(p.city) : '';
  const dist =
    p.distanceKm === null ? null : p.distanceKm < 1 ? (compact ? '< 1 km' : t('distance.under1')) : compact ? `${p.distanceKm} km` : t('distance.km', { km: p.distanceKm });
  if (compact) {
    return (
      <span className="inline-flex min-w-0 items-center gap-1 text-sm text-tg-hint">
        <MapPin size={13} className="shrink-0" />
        <span className="truncate">{dist ?? place}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-sm text-tg-hint">
      <MapPin size={13} className="shrink-0" />
      <span className="truncate">{[dist, place].filter(Boolean).join(' · ')}</span>
    </span>
  );
}

export function fromPrice(p: ProviderCard) {
  return p.prices.length ? Math.min(...p.prices.map((x) => x.price)) : null;
}

/** Page header with a back arrow, like Telegram/Instagram. */
export function TopBar({ title, right, onBack }: { title?: ReactNode; right?: ReactNode; onBack?: () => void }) {
  const nav = useNav();
  return (
    <div className="sticky top-0 z-20 flex h-12 items-center gap-1 border-b border-black/5 bg-tg-bg px-1">
      <button onClick={onBack ?? nav.pop} className="flex h-10 w-10 items-center justify-center rounded-full active:bg-tg-secondary" aria-label="Back">
        <ChevronLeft size={26} />
      </button>
      <div className="min-w-0 flex-1 truncate text-[17px] font-semibold">{title}</div>
      {right}
    </div>
  );
}

export function EmptyState({ icon, text, action }: { icon: ReactNode; text: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-10 py-20 text-center text-tg-hint">
      <div className="text-5xl">{icon}</div>
      <p>{text}</p>
      {action}
    </div>
  );
}

/** Bottom sheet that slides up, with a drag handle look. */
export function BottomSheet({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  return (
    <Sheet opened={open} onBackdropClick={onClose} className="rounded-t-3xl pb-safe" style={{ zIndex: 60 }}>
      <div className="mx-auto mb-1 mt-2.5 h-1.5 w-10 rounded-full bg-black/15" />
      <div className="max-h-[80vh] overflow-y-auto px-5 pb-6 pt-2">{children}</div>
    </Sheet>
  );
}

export function PrimaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'danger' | 'soft' }) {
  const { tone = 'primary', className = '', ...rest } = props;
  const tones = {
    primary: 'bg-tg-button text-tg-buttonText',
    danger: 'bg-red-500 text-white',
    soft: 'bg-tg-secondary text-tg-text',
  };
  return (
    <button
      {...rest}
      className={`flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-[16px] font-semibold active:opacity-80 disabled:opacity-50 ${tones[tone]} ${className}`}
    />
  );
}

/** Small message at the bottom of the screen for a couple of seconds. */
const ToastContext = createContext<(msg: string) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number>();
  const show = useCallback((m: string) => {
    setMsg(m);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(null), 2500);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      {msg && (
        <div className="pointer-events-none fixed inset-x-0 bottom-24 z-[100] flex justify-center px-6">
          <div className="rounded-2xl bg-black/80 px-4 py-2.5 text-center text-sm text-white shadow-lg">{msg}</div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/** "5 min", "2 h", "3 d" since a time, or the clock time today. */
export function timeLabel(iso: string, lang: string) {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString(lang === 'am' ? 'am-ET' : 'en-US', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString(lang === 'am' ? 'am-ET' : 'en-US', { month: 'short', day: 'numeric' });
}
